import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import i18next from 'i18next';
import type { SortOption } from '../../@types/ElasticSearchResponse';
import type QueryFilter from '../../helpers/query-filter';
import { getMarket } from '../../i18n';
import { getQueryFilter } from '../../utils/reselectors';
import SortOptionsModal from './sort-options-modal';

export const getOptionDisplayName = (displayName: string) => {
  switch (displayName) {
    case 'Pris lägst':
      return i18next.t('sort.priceAsc', { ns: 'search' });
    case 'Pris högst':
      return i18next.t('sort.priceDesc', { ns: 'search' });
    case 'Årsmodell nyast':
      return i18next.t('sort.modelYearAsc', { ns: 'search' });
    case 'Årsmodell äldst':
      return i18next.t('sort.modelYearDesc', { ns: 'search' });
    case 'Miltal lägst':
      return i18next.t('sort.mileageAsc', { ns: 'search' });
    case 'Miltal högst':
      return i18next.t('sort.mileageDesc', { ns: 'search' });
    case 'Senaste':
      return i18next.t('sort.latest', { ns: 'search' });
    default:
      return displayName;
  }
};

interface TypeFilterBarProps {
  queryFilter?: QueryFilter;
  sortOptions?: SortOption[];
  disableCurrentIndicator?: boolean;
  onApplySort: (query: string) => void;
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const TypeFilterBar = ({
  queryFilter: _queryFilter,
  sortOptions,
  disableCurrentIndicator,
  onApplySort,
  onRequestSearch,
}: TypeFilterBarProps) => {
  const { t } = useTranslation(['common', 'search']);
  const queryFilter = _queryFilter || useSelector(getQueryFilter);

  const [state, setState] = useState({ sortModal: false, subscribeModal: false });
  const onToggleOptions = () => setState({ ...state, sortModal: !state.sortModal });

  const marketCode = getMarket().marketCode;
  const showAuction = marketCode === 'SE';
  const showBusinessLeasing = marketCode === 'SE';

  const AdTypes = [
    {
      displayName: t('All', { ns: 'common' }),
      isActive: (queryFilter: QueryFilter) =>
        !queryFilter.has('sellerType') &&
        !queryFilter.has('isAuctionItem') &&
        !queryFilter.has('leasing') &&
        !queryFilter.has('businessLeasing'),
      getQueryFilter: (queryFilter: QueryFilter) =>
        queryFilter
          .remove('sellerType')
          .remove('isAuctionItem')
          .remove('businessLeasing')
          .remove('leasing'),
    },
    {
      displayName: t('Private leasing', { ns: 'search' }),
      isActive: (queryFilter: QueryFilter) => queryFilter.contains('leasing', 'true'),
      getQueryFilter: (queryFilter: QueryFilter) =>
        queryFilter
          .remove('sellerType')
          .remove('isAuctionItem')
          .remove('businessLeasing')
          .remove('leasing')
          .add('leasing', 'true'),
    },
  ];

  if (showAuction) {
    AdTypes.push({
      displayName: t('Auction', { ns: 'search' }),
      isActive: (queryFilter: QueryFilter) => queryFilter.contains('isAuctionItem', 'true'),
      getQueryFilter: (queryFilter: QueryFilter) => {
        if (queryFilter.has('isAuctionItem')) {
          return queryFilter
            .remove('sellerType')
            .remove('isAuctionItem')
            .remove('leasing')
            .remove('businessLeasing');
        }
        return queryFilter
          .remove('sellerType')
          .remove('leasing')
          .remove('businessLeasing')
          .add('isAuctionItem', 'true');
      },
    });
  }

  if (showBusinessLeasing) {
    AdTypes.push({
      displayName: t('Business leasing', { ns: 'search' }),
      isActive: (queryFilter: QueryFilter) => queryFilter.contains('businessLeasing', 'true'),
      getQueryFilter: (queryFilter: QueryFilter) =>
        queryFilter
          .remove('sellerType')
          .remove('isAuctionItem')
          .remove('leasing')
          .remove('businessLeasing')
          .add('businessLeasing', 'true'),
    });
  }

  const sortDisplayName = useMemo(() => {
    const activeSortOption = sortOptions?.find((sortOption) => sortOption.selected) || undefined;
    const activeSortOptionDisplayName = getOptionDisplayName(
      activeSortOption?.displayName || t('Sort', { ns: 'common' }),
    );

    return activeSortOptionDisplayName;
  }, [sortOptions]);

  return (
    <section className="page-section page-section-thin">
      {state.sortModal && sortOptions && (
        <SortOptionsModal
          onClose={onToggleOptions}
          sortOptions={sortOptions}
          onApplySort={onApplySort}
        />
      )}
      <div className="hidden-md" />
      <div data-am-columnrow="">
        <div className="column">
          <div data-wx-togglelist="">
            <div className="toggle-list-overflow">
              <ul className="toggle-list">
                {AdTypes.map((adType) => (
                  <li key={adType.displayName} className="toggle-list-item">
                    <button
                      type="button"
                      className={`toggle-list-action${
                        adType.isActive(queryFilter) && !disableCurrentIndicator ? ' is-active' : ''
                      }`}
                      onClick={() => onRequestSearch(adType.getQueryFilter(queryFilter))}
                      title={adType.displayName}
                    >
                      {adType.displayName}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="column minimal">
          <button
            type="button"
            data-wx-inlinebutton=""
            onClick={onToggleOptions}
            title={t('Sort', { ns: 'common' })}
          >
            <div className="inline-button-icon">
              <i className="icon-sort no-margin" />
            </div>
            <div className="inline-button-text">{sortDisplayName}</div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default TypeFilterBar;
